import type { BusyBlockView, ScheduleResult } from "@planerius/contracts/cabinet"

import type { ApiProblem } from "../../api/problem"
import { freeWindows, workWindow } from "../../journal/free-slots"
import { formatMinute, formatRange, minutesFromDayStart } from "../../journal/journal-time"
import { minutesLabel } from "../../lib/plural"

/**
 * «Перерыв» мастера (5-М5, ТЗ 05 §5.7) как данные, отдельно от разметки.
 *
 * ЭКРАН ОТВЕЧАЕТ НА ОДИН ВОПРОС: закрыть себе время, чтобы туда не записали. Одной
 * кнопкой — «30 минут прямо сейчас», а не формой из трёх полей (§5.7, «Узкий экран»).
 * Поэтому главное здесь — ближайшее свободное время под каждую длительность, посчитанное
 * заранее, чтобы кнопка сразу говорила, ЧТО именно она закроет: «30 минут · 13:30–14:00».
 *
 * СВОБОДНОЕ ВРЕМЯ — ПОДСКАЗКА, А НЕ БРОНЬ. Пока мастер выбирал, на это время могли
 * записать клиента: сервер ответит «время занято», и экран обязан предложить следующее
 * окно, а не показать ошибку. То же с концом смены: сервер отвергает перерыв, выходящий
 * за смену, и экран предлагает подрезать его по концу смены (§5.7, «Состояния»).
 */

/** Длительности одним касанием — ровно те, что в ТЗ. */
export const BREAK_DURATIONS = [15, 30, 60] as const

export interface BreakOption {
  readonly minutes: number
  readonly startMinute: number
  readonly endMinute: number
  /** Подпись кнопки: длительность и точный интервал. Кнопка говорит, что она сделает. */
  readonly label: string
}

/**
 * Ближайшее свободное время под каждую длительность.
 *
 * Не раньше текущей минуты: перерыв задним числом сервер отвергнет, и это правильно —
 * прошедший перерыв ничего не закрывает. Длительности, под которые в смене места нет,
 * в список не попадают: кнопка, которая заведомо откажет, хуже отсутствующей.
 */
export function breakOptions(input: {
  readonly result: ScheduleResult
  readonly date: string
  readonly employeeId: string
  readonly nowMinute: number
}): readonly BreakOption[] {
  const options: BreakOption[] = []
  for (const minutes of BREAK_DURATIONS) {
    const [first] = freeWindows({
      result: input.result,
      date: input.date,
      employeeId: input.employeeId,
      durationMinutes: minutes,
      // Шаг пять минут, а не шаг сетки филиала: перерыв «прямо сейчас» не должен ждать
      // ближайшей четверти часа.
      stepMinutes: 5,
      notBeforeMinute: input.nowMinute,
      limit: 1,
    })
    if (first === undefined) continue
    options.push({
      minutes,
      startMinute: first,
      endMinute: first + minutes,
      label: `${minutesLabel(minutes)} · ${formatRange(first, first + minutes)}`,
    })
  }
  return options
}

export interface OwnBreak {
  readonly id: string
  readonly startMinute: number
  readonly endMinute: number
  readonly note: string | null
  readonly version: string
  /** Уже закончился — снять его нечем и незачем. */
  readonly past: boolean
}

/** Свои перерывы этого дня: их мастер вправе снять (§5.7 «Что делает»). */
export function ownBreaks(input: {
  readonly result: ScheduleResult
  readonly date: string
  readonly employeeId: string
  readonly nowMinute: number
}): readonly OwnBreak[] {
  const zone = input.result.branch.timezone
  return input.result.busy
    .filter((block: BusyBlockView) => block.employeeId === input.employeeId && block.kind === "break")
    .map((block) => {
      const startMinute = minutesFromDayStart(block.startAt, zone, input.date)
      const endMinute = minutesFromDayStart(block.endAt, zone, input.date)
      return {
        id: block.id,
        startMinute,
        endMinute,
        note: block.note,
        version: block.updatedAt,
        past: endMinute <= input.nowMinute,
      }
    })
    .toSorted((a, b) => a.startMinute - b.startMinute)
}

/** Что предложить вместо отказа. null — предложить нечего, показать текст. */
export interface BreakRecovery {
  readonly text: string
  readonly retry: { readonly startMinute: number; readonly endMinute: number } | null
  readonly retryLabel: string | null
}

/**
 * Отказ сервера → понятная фраза и, где возможно, следующее действие одним касанием.
 *
 * Тексты из ТЗ §5.7 «Состояния и конфликты», слово в слово по смыслу:
 *   — «Пока вы выбирали, на 15:00 записался клиент. Свободно с 15:45.»
 *   — «Смена заканчивается в 20:00. Поставить перерыв до 20:00?»
 */
export function recoverBreak(input: {
  readonly problem: ApiProblem
  readonly attempt: { readonly startMinute: number; readonly endMinute: number }
  readonly result: ScheduleResult
  readonly date: string
  readonly employeeId: string
  readonly nowMinute: number
}): BreakRecovery {
  const { problem, attempt } = input
  const minutes = attempt.endMinute - attempt.startMinute

  if (problem.code === "busy_conflicts") {
    // Данные дня на экране — те, на которых человек промахнулся: новой записи в них нет,
    // и окно по ним снова указало бы на занятое время. Поэтому ищем ПОСЛЕ конфликтов,
    // которые назвал сервер; если он их не перечислил — хотя бы не с той же минуты.
    const zone = input.result.branch.timezone
    const conflictEnd = problem.conflicts.reduce(
      (latest, conflict) => Math.max(latest, minutesFromDayStart(conflict.endAt, zone, input.date)),
      Number.NEGATIVE_INFINITY,
    )
    const notBefore = Math.max(
      input.nowMinute,
      Number.isFinite(conflictEnd) ? conflictEnd : attempt.startMinute + 5,
    )
    const [next] = freeWindows({
      result: input.result,
      date: input.date,
      employeeId: input.employeeId,
      durationMinutes: minutes,
      stepMinutes: 5,
      notBeforeMinute: notBefore,
      limit: 1,
    })
    const taken = `Пока вы выбирали, на ${formatMinute(attempt.startMinute)} записался клиент.`
    if (next === undefined) {
      return { text: `${taken} До конца смены нет свободного окна на ${minutesLabel(minutes)}.`, retry: null, retryLabel: null }
    }
    return {
      text: `${taken} Свободно с ${formatMinute(next)}.`,
      retry: { startMinute: next, endMinute: next + minutes },
      retryLabel: `Поставить с ${formatMinute(next)}`,
    }
  }

  if (problem.code === "break_outside_shift") {
    const work = workWindow({ result: input.result, date: input.date, employeeId: input.employeeId })
    if (work !== null && attempt.startMinute < work.endMinute && attempt.startMinute >= work.startMinute) {
      return {
        text: `Смена заканчивается в ${formatMinute(work.endMinute)}. Поставить перерыв до ${formatMinute(work.endMinute)}?`,
        retry: { startMinute: attempt.startMinute, endMinute: work.endMinute },
        retryLabel: `Поставить до ${formatMinute(work.endMinute)}`,
      }
    }
    return { text: "Перерыв ставится внутри рабочей смены.", retry: null, retryLabel: null }
  }

  if (problem.code === "busy_in_past") {
    return { text: "Это время уже прошло. Выберите время начиная с текущего.", retry: null, retryLabel: null }
  }

  if (problem.code === "permission_denied" || problem.code === "forbidden") {
    return {
      text: "Перерывы в этом салоне ставит администратор. Попросите его закрыть время.",
      retry: null,
      retryLabel: null,
    }
  }

  return { text: problem.detail ?? problem.title, retry: null, retryLabel: null }
}
